<template>
  <v-card outlined class="rounded-xl">
    <v-card-text v-if="loadError">
      <v-alert type="error" outlined class="rounded-xl mb-0">
        <div class="font-weight-medium">Notification settings could not be loaded</div>
        <div class="text-body-2 mb-3">Check your connection and try again.</div>
        <v-btn color="primary" small min-height="44" @click="loadNotifications()">Try again</v-btn>
      </v-alert>
    </v-card-text>

    <v-card-text v-else-if="!isLoading">
      <section aria-labelledby="mobile-alerts-heading" class="pb-4">
        <h3 id="mobile-alerts-heading" class="text-subtitle-1 font-weight-bold mb-1">Mobile app alerts</h3>
        <p class="admin-muted mb-3">Choose which alerts you receive in the mobile app for this location.</p>
        <div v-for="(preference, index) in mobileAlertPreferences" :key="preference.key">
          <v-row align="center" no-gutters class="py-2">
            <v-col cols="12" sm="8">
              <div class="font-weight-bold">{{ preference.title }}</div>
              <div class="admin-muted">{{ preference.description }}</div>
            </v-col>
            <v-col cols="12" sm="4" class="d-flex justify-start justify-sm-end pt-2 pt-sm-0">
              <v-switch
                :input-value="preference.enabled"
                :loading="pushSavingKey === preference.key"
                :disabled="pushSavingKey !== null"
                color="primary"
                inset
                hide-details
                :label="preference.enabled ? 'Enabled' : 'Disabled'"
                :aria-label="`${preference.enabled ? 'Disable' : 'Enable'} ${preference.title.toLowerCase()}`"
                @change="setMobileAlertPreference(preference, $event)"
              />
            </v-col>
          </v-row>
          <v-divider v-if="index < mobileAlertPreferences.length - 1" />
        </div>
      </section>

      <v-divider class="mb-4" />

      <section v-if="isShiftEnabled" aria-labelledby="email-alerts-heading" class="pb-4">
        <h3 id="email-alerts-heading" class="text-subtitle-1 font-weight-bold mb-1">Email alerts</h3>
        <p class="admin-muted mb-3">Choose who receives operational summaries by email.</p>
        <v-row align="center">
          <v-col>
            <div class="font-weight-bold">Batch close summary</div>
            <div class="admin-muted">Email a summary when the batch day is closed.</div>
            <div class="admin-muted mt-1">
              Currently {{ emailInfoList.length }} Email(s) set.
            </div>
          </v-col>
          <v-col cols="12" sm="auto" class="d-flex align-center">
            <v-btn v-if="closeBatchNotification.enabled" class="v-btn-with-radius mr-2" color="primary" dark small min-height="44" @click="openEmailDialog">
              <v-icon left small>mdi-pencil</v-icon>Edit
            </v-btn>
            <v-btn v-if="!closeBatchNotification.enabled" class="v-btn-with-radius mr-2" color="primary" small min-height="44" @click="switchBatchEmail">
              <v-icon left small>mdi-email-plus</v-icon>Enable
            </v-btn>
            <v-btn v-else class="v-btn-with-radius" color="error" small min-height="44" @click="switchBatchEmail">
              <v-icon left small>mdi-email-off</v-icon>Disable
            </v-btn>
          </v-col>
        </v-row>
        <v-divider v-if="textNotifications.length" class="mt-3" />
      </section>

      <section v-if="textNotifications.length" aria-labelledby="text-alerts-heading">
        <h3 id="text-alerts-heading" class="text-subtitle-1 font-weight-bold mb-1">Text message alerts</h3>
        <p class="admin-muted mb-3">Configure sales and operational alerts sent by text message.</p>
      </section>

      <div v-for="(notification, idx) in textNotifications" :key="notification.id" class="pb-4">
        <SaleAlertNotification
          v-if="notification.stringId === 'SALESALERT'"
          :notification="notification"
          :LocationId="LocationId"
          :location="location"
          @reload="loadNotifications"
        />
        <v-divider v-if="idx < textNotifications.length - 1" class="my-3" />
      </div>
    </v-card-text>

    <v-card-text v-else class="d-flex justify-center pa-6">
      <v-progress-circular indeterminate color="primary" />
    </v-card-text>

    <v-dialog persistent v-model="emailDialog" max-width="600px">
      <v-card rounded="xxl" class="d-flex flex-column" max-height="90vh">
        <v-toolbar flat dense color="primary" dark class="flex-grow-0">
          <v-toolbar-title>Batch close notification emails</v-toolbar-title>
          <v-spacer />
          <v-btn icon aria-label="Close" @click="cancelDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="overflow-y-auto pa-4 pa-sm-6">
          <v-row align="start">
            <v-col cols="12" sm="4">
              <v-text-field v-model="emailInfo.name" label="Name" :hide-details="nameErrors.length === 0" :error-messages="nameErrors" outlined dense />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="emailInfo.email" label="Email" :hide-details="emailErrors.length === 0" :error-messages="emailErrors" outlined dense />
            </v-col>
            <v-col cols="12" sm="2">
              <v-btn dark color="primary" :disabled="$v.emailInfo.$invalid" @click="addEmail">
                <v-icon left small>mdi-plus</v-icon>Add
              </v-btn>
            </v-col>
          </v-row>
          <v-list v-if="emailInfoList.length" dense>
            <v-divider />
            <v-list-item v-for="(row, index) in emailInfoList" :key="`${row.email}-${index}`" two-line>
              <v-list-item-content>
                <v-list-item-title>{{ row.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ row.email }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon color="error" :aria-label="`Remove ${row.email}`" @click="removeEmail(index)"><v-icon>mdi-delete</v-icon></v-btn>
              </v-list-item-action>
            </v-list-item>
            <v-divider />
          </v-list>
        </v-card-text>
        <v-card-actions class="justify-end flex-grow-0">
          <v-btn text @click="cancelDialog">Cancel</v-btn>
          <v-btn color="primary" @click="emailsUpdated">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import SaleAlertNotification from "@/modules/Location/components/notification/SaleAlertNotification.vue";
import locationService from "@/modules/Location/service.js";
import settingsService from "../../Settings/service";
import { getMobilePushPreference, setMobilePushPreference } from "@/mobile/service";
import { validationMixin } from "vuelidate";
import { required, email } from "vuelidate/lib/validators";

export default {
  name: "location-view-notifications",
  props: ["LocationId", "location"],
  components: { SaleAlertNotification },
  mixins: [validationMixin],
  data() {
    return {
      notifications: [],
      registerSettings: {
        shiftEnabled: false,
        metadata: { closeShiftNotification: { enabled: false, emails: [] } },
      },
      isLoading: false,
      emailDialog: false,
      emailInfo: { name: null, email: null },
      emailInfoList: [],
      pushSavingKey: null,
      loadError: false,
      mobileAlertPreferences: [
        { key: "shift-close", title: "Shift close", description: "Receive an alert when a shift is closed.", enabled: false },
        { key: "store-open", title: "Store open", description: "Receive an alert when this location opens for business.", enabled: false },
        { key: "sales-summary", title: "Scheduled sales summaries", description: "Receive sales summaries at this location's configured alert times.", enabled: false },
        { key: "invoice-delete", title: "Invoice and estimate deletion", description: "Receive an alert when an invoice or estimate is deleted.", enabled: false },
      ],
    };
  },
  computed: {
    closeBatchNotification() {
      return this.registerSettings.metadata.closeShiftNotification;
    },
    isShiftEnabled() {
      return this.registerSettings.shiftEnabled;
    },
    textNotifications() {
      return this.notifications.filter((notification) => notification.stringId === "SALESALERT");
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.emailInfo.name.$dirty) return errors;
      if (!this.$v.emailInfo.name.required) errors.push("Field is required");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.emailInfo.email.$dirty) return errors;
      if (!this.$v.emailInfo.email.required) errors.push("Field is required");
      if (!this.$v.emailInfo.email.email) errors.push("Email is not valid");
      return errors;
    },
  },
  validations: { emailInfo: { name: { required }, email: { required, email } } },
  created() {
    this.loadNotifications();
  },
  methods: {
    async loadNotifications(silent = false) {
      try {
        if (!silent) this.isLoading = true;
        this.loadError = false;
        const [{ data: allNotifications }, { data: currentSettings }, registerResponse, mobilePreferences] = await Promise.all([
          settingsService.Notification.getAll(),
          locationService.Settings.Notifications.get(this.LocationId),
          locationService.Settings.Register.get(this.LocationId),
          Promise.all(this.mobileAlertPreferences.map((preference) => getMobilePushPreference(preference.key, this.LocationId))),
        ]);
        this.mobileAlertPreferences = this.mobileAlertPreferences.map((preference, index) => {
          const saved = mobilePreferences[index];
          return { ...preference, enabled: Boolean(saved && saved.enabled) };
        });
        this.notifications = allNotifications
          .filter((notification) => notification.stringId !== "NIGHTLYSALE")
          .map((notification) => {
            const current = currentSettings.find((setting) => setting.NotificationId === notification.id) || null;
            return { ...notification, NotificationSetting: current };
          });

        const settings = registerResponse.data || {};
        const metadata = settings.metadata || {};
        this.registerSettings = {
          ...this.registerSettings,
          ...settings,
          metadata: {
            ...this.registerSettings.metadata,
            ...metadata,
            closeShiftNotification: {
              enabled: false,
              emails: [],
              ...(metadata.closeShiftNotification || {}),
            },
          },
        };
        this.emailInfoList = [...(this.closeBatchNotification.emails || [])];
      } catch (err) {
        this.loadError = true;
        console.error("Error loading notifications:", err);
      } finally {
        if (!silent) this.isLoading = false;
      }
    },
    openEmailDialog() {
      this.emailInfoList = [...(this.closeBatchNotification.emails || [])];
      this.emailDialog = true;
    },
    cancelDialog() {
      this.resetEmailForm();
      this.emailInfoList = [...(this.closeBatchNotification.emails || [])];
      this.emailDialog = false;
    },
    resetEmailForm() {
      this.emailInfo = { name: null, email: null };
      this.$v.$reset();
    },
    addEmail() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      if (this.emailInfoList.some((row) => row.email === this.emailInfo.email)) {
        this.showError("Email already exists");
        return;
      }
      if (this.emailInfoList.length >= 5) {
        this.showError("Cannot store more than 5 emails");
        return;
      }
      this.emailInfoList.push({ ...this.emailInfo });
      this.resetEmailForm();
    },
    removeEmail(index) {
      this.emailInfoList.splice(index, 1);
    },
    async switchBatchEmail() {
      this.closeBatchNotification.enabled = !this.closeBatchNotification.enabled;
      await this.updateRegisterSettings();
    },
    async emailsUpdated() {
      if (!this.emailInfoList.length) {
        this.showError("Please add at least one email");
        return;
      }
      this.closeBatchNotification.emails = [...this.emailInfoList];
      if (await this.updateRegisterSettings()) this.cancelDialog();
    },
    async updateRegisterSettings() {
      this.$Progress.start();
      try {
        await locationService.Settings.Register.update(this.LocationId, this.registerSettings);
        this.$Progress.finish();
        this.$toasted.success("Setting was updated", { position: "top-right", icon: "check", duration: 2000 });
        return true;
      } catch (error) {
        this.$Progress.fail();
        this.showError(error?.data?.message || error.message);
        await this.loadNotifications(true);
        return false;
      }
    },
    async setMobileAlertPreference(preference, enabled) {
      this.pushSavingKey = preference.key;
      try {
        const saved = await setMobilePushPreference(preference.key, this.LocationId, enabled);
        preference.enabled = Boolean(saved && saved.enabled);
        this.showSuccess(`${preference.title} app alerts ${preference.enabled ? "enabled" : "disabled"}`);
      } catch (error) {
        this.showError(`${preference.title} app alert preference could not be saved`);
      } finally {
        this.pushSavingKey = null;
      }
    },
    showError(message) {
      this.$toasted.error(message, { position: "top-right", icon: "error", duration: 2000 });
    },
    showSuccess(message) {
      this.$toasted.success(message, { position: "top-right", icon: "check", duration: 2000 });
    },
  },
};
</script>
